import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { LogoWordmark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "In Review", "Partner Assigned", "Archived"];
const TOKEN_KEY = "pjg_admin_token";

const inputCls =
  "w-full bg-[#111111] border border-[#2A2A2A] text-[#F2F1EE] text-sm px-4 py-3.5 placeholder:text-[#8A8A88]/60 focus:outline-none focus:border-[#E8E8E6]/60 transition-colors duration-300";

const statusStyle = (s) =>
  ({
    New: "text-[#E8E8E6] border-[#E8E8E6]/40",
    "In Review": "text-[#C9C9C7] border-[#C9C9C7]/30",
    "Partner Assigned": "text-emerald-400 border-emerald-400/30",
    Archived: "text-[#8A8A88] border-[#8A8A88]/30",
  })[s] || "text-[#C9C9C7] border-[#2A2A2A]";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0A0A0A]">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-[#2A2A2A] bg-[#161616]/60 backdrop-blur-md p-10"
      >
        <LogoWordmark dark />
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.28em] text-[#E8E8E6]">Partner Access</p>
        <h1 className="mt-3 font-serif text-3xl text-[#F2F1EE]">Enquiries Console</h1>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#8A8A88] mb-2">Email</label>
            <input id="admin-email" type="email" required data-testid="admin-login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@pjgcp.com" className={inputCls} />
          </div>
          <div>
            <label htmlFor="admin-password" className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#8A8A88] mb-2">Password</label>
            <input id="admin-password" type="password" required data-testid="admin-login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#F2F1EE] text-[#0A0A0A] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#E8E8E6] disabled:opacity-60 transition-colors duration-300"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
        <Link to="/" className="mt-6 inline-block font-mono text-[10px] uppercase tracking-[0.24em] text-[#8A8A88] hover:text-[#E8E8E6] transition-colors">
          ← Return to site
        </Link>
      </motion.div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "All" ? { status: filter } : {};
      const { data } = await axios.get(`${API}/enquiries`, { headers, params });
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Could not load enquiries");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, token]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}/status`, { status }, { headers });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="border-b border-[#2A2A2A]/60 bg-[#0A0A0A]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <LogoWordmark compact dark />
          <div className="flex items-center gap-4">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C9C9C7]/80 hover:text-[#E8E8E6] transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C9C9C7]/80 hover:text-[#E8E8E6] transition-colors"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#E8E8E6]">Confidential</p>
            <h1 className="mt-3 font-serif text-4xl text-[#F2F1EE]">Enquiries Console</h1>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
            {["All", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`admin-filter-${s.toLowerCase().replace(/ /g, "-")}`}
                className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                  filter === s
                    ? "border-[#E8E8E6] text-[#E8E8E6]"
                    : "border-[#2A2A2A] text-[#8A8A88] hover:border-[#E8E8E6]/50 hover:text-[#C9C9C7]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10" data-testid="admin-submissions-table">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#8A8A88]">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : enquiries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 border border-[#2A2A2A] bg-[#111111]" data-testid="admin-empty-state">
              <Inbox size={36} className="text-[#8A8A88]" strokeWidth={1.4} />
              <p className="mt-4 text-sm text-[#8A8A88]">No enquiries {filter !== "All" ? `marked “${filter}”` : "yet"}.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {enquiries.map((enq) => (
                <motion.article
                  key={enq.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`admin-enquiry-${enq.id}`}
                  className="border border-[#2A2A2A] bg-[#111111] p-6 lg:p-8"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-xl text-[#F2F1EE]">{enq.name}</h3>
                      <p className="mt-1 text-sm text-[#8A8A88]">{enq.organization}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${statusStyle(enq.status)}`} data-testid={`admin-enquiry-status-${enq.id}`}>
                      {enq.status}
                    </span>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8A8A88]">Email</p>
                      <a href={`mailto:${enq.email}`} className="mt-1 block text-[#C9C9C7] hover:text-[#E8E8E6] transition-colors break-all">{enq.email}</a>
                    </div>
                    {enq.phone && (
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8A8A88]">Telephone</p>
                        <p className="mt-1 text-[#C9C9C7]">{enq.phone}</p>
                      </div>
                    )}
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8A8A88]">Nature</p>
                      <p className="mt-1 text-[#C9C9C7]">{enq.transaction_type || "—"}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8A8A88]">Scale</p>
                      <p className="mt-1 text-[#C9C9C7]">{enq.estimated_value || "—"}</p>
                    </div>
                  </div>

                  <p className="mt-5 border-l-2 border-[#E8E8E6]/40 pl-4 text-sm leading-relaxed text-[#C9C9C7]/85 whitespace-pre-wrap">
                    {enq.message}
                  </p>

                  <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                    <p className="font-mono text-[10px] tracking-[0.18em] text-[#8A8A88]">
                      {new Date(enq.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.filter((s) => s !== enq.status).map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatus(enq.id, s)}
                          data-testid={`admin-enquiry-${enq.id}-set-${s.toLowerCase().replace(/ /g, "-")}`}
                          className="rounded-full border border-[#2A2A2A] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#8A8A88] hover:border-[#E8E8E6]/60 hover:text-[#E8E8E6] transition-colors duration-300"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return token ? (
    <Dashboard token={token} onLogout={logout} />
  ) : (
    <LoginForm onLogin={(t) => setToken(t)} />
  );
}
